import NoSerialize           from "@/core/misc/decorators/NoSerialize";
import Title_ru_RU_Class     from "@/core/misc/decorators/Title_ru_RU_Class";
import Title_ru_RU           from '@/core/misc/decorators/Title_ru_RU';
import TYPES                 from '@/core/services/Changelogs/types';

import Core           		   from "@/core/Core";
import AbstractDemand 		   from "@/domains/Abstract/AbstractDemand";
import DateSimple       	   from "@/core/types/DateSimple";

import type Tender           from "@/domains/Tenders/Tender";

const REASON_UNAVAILABLE = {
  ALREADY_HAS_TENDER:     'По этой потребности уже есть тендер',
  TENDER_HAS_NOT_CREATED: 'Тендер ещё не создан',
  QTY_EXHAUSTED:          'Потребность исчерпана',
  NO_CATEGORY:            'Потребность без категории',
  BEEN_DELETED:           'Потребность уже удалена',
} as const;

@Title_ru_RU_Class('Потребность')                   // demand → Demand
class Demand extends AbstractDemand {
  static Events = {
    Created: 'demand:created',
    Updated: 'demand:updated',
    Deleted: 'demand:deleted',
  } as const;

  isBookmarked = null;

  @Title_ru_RU('Дата поставки', TYPES.DATE)
  desiredReceiptDate: DateSimple;

  @NoSerialize
  tender: Tender;

  static fromServer(data: any, result = new Demand()): Demand {
    super.fromServer(data, result);

    result.desiredReceiptDate = Core.utils.hydrate(data.desiredReceiptDate, DateSimple.fromServer)!;

    return result;
  }

  static fromNew(result = new Demand): Demand {
    super.fromNew(result);

    //

    return result;
  }

  /** Метод используется в диалоге создания потребности, когда мы создаём пачкой сразу несколько */
  static fromAnotherDemand(anotherDemand: Demand, result = new Demand()): Demand {
    super.fromAnotherDemand(anotherDemand, result);

    result.desiredReceiptDate     = Core.utils.cloneDeep(anotherDemand.desiredReceiptDate);

    return result;
  }

  get hasTender():boolean {
    return this.tender?.id !== null && this.tender?.id !== undefined;
  }

  /** ================ Валидация действий ================ */

  getReasonCannotCreateTender(): string | null {
    if (this.tender) return REASON_UNAVAILABLE.ALREADY_HAS_TENDER;
    return null;
  }

  getReasonCannotShowTender(): string | null {
    if (!this.tender) return REASON_UNAVAILABLE.TENDER_HAS_NOT_CREATED;
    return null;
  }

  getReasonCannotCompareOffers(): string | null {
    if (this.qtyLeft.isZero()) return REASON_UNAVAILABLE.QTY_EXHAUSTED;
    if (!this.productCategory) return REASON_UNAVAILABLE.NO_CATEGORY;
    return null;
  }

  getReasonCannotDelete(): string | null {
    if (this.isDeleted === true) return REASON_UNAVAILABLE.BEEN_DELETED;
    return null;
  }

  /**
   * Дата, по которой считается срочность поставки:
   * предзаказ — начало окна поставки, обычная потребность — желаемая дата.
   * null, если данных нет.
   */

  get relevantDeliveryDate(): DateSimple | null {
    if (this.isPreorderNeeded) {
      return this.preorderShipmentPeriod?.start ?? null;
    }
    else {
      return this.desiredReceiptDate ?? null;
    }
  }

  /**
   * Разница в днях между датой поставки и сегодня.
   * Отрицательное значение — просрочка. null, если даты нет.
   */

  get deliveryDaysLeft(): number | null {
    if (this.relevantDeliveryDate === null) {
      return null;
    }
    else {
      return this.relevantDeliveryDate.diffInDays(DateSimple.today());
    }
  }

  /**
   * CSS-класс для ячейки даты поставки:
   * 'delivery-date-overdue' — просрочена, 'delivery-date-warning' — осталось ≤3 дней.
   * null, если подсветка не нужна.
   */

  get deliveryDateClass(): string | null {
    const daysLeft = this.deliveryDaysLeft;
    if (daysLeft === null) return null;

    if (daysLeft < 0) return 'delivery-date-overdue';
    if (daysLeft <= AbstractDemand.DELIVERY_DATE_WARNING_DAYS) return 'delivery-date-warning';
    return null;
  }

  /**
   * Текст тултипа для подсвеченной даты поставки. null, если подсветка не нужна.
   */

  get deliveryDateTooltip(): string | null {
    const daysLeft = this.deliveryDaysLeft;
    if (daysLeft === null) return null;

    if (daysLeft < 0) {
      return 'Дата поставки просрочена';
    }
    if (daysLeft <= AbstractDemand.DELIVERY_DATE_WARNING_DAYS) {
      return `До даты поставки осталось ${daysLeft} ${
        Core.utils.regional.ru.pluralize(daysLeft, 'день', 'дня', 'дней')
      }`;
    }
    return null;
  }
}

export default Demand;
